import React, { createContext, useCallback, useContext, useMemo, useState } from "react";

const CompanyContext = createContext();
const SELECTED_COMPANY_STORAGE_KEY = "selectedCompanyId";

function readStoredSelectedCompanyId() {
  if (typeof window === "undefined") return "";
  return window.sessionStorage.getItem(SELECTED_COMPANY_STORAGE_KEY) || "";
}

function writeStoredSelectedCompanyId(id) {
  if (typeof window === "undefined") return;
  if (id) {
    window.sessionStorage.setItem(SELECTED_COMPANY_STORAGE_KEY, id);
  } else {
    window.sessionStorage.removeItem(SELECTED_COMPANY_STORAGE_KEY);
  }
}

export function CompanyProvider({ children }) {
  // Companies state: id, name, address, tel
  const [companies, setCompanies] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState(readStoredSelectedCompanyId);
  const [refreshTick, setRefreshTick] = useState(0);

  // Set companies from API
  const setCompanyList = useCallback((companyList) => {
    const safeList = Array.isArray(companyList) ? companyList : [];

    setCompanies((prevCompanies) => {
      if (
        prevCompanies.length === safeList.length &&
        prevCompanies.every((item, index) => item?.id === safeList[index]?.id)
      ) {
        return prevCompanies;
      }
      return safeList;
    });

    setSelectedCompanyId((prevSelectedCompanyId) => {
      const storedValue = readStoredSelectedCompanyId();
      const currentSelection = prevSelectedCompanyId || storedValue || "";

      if (safeList.length === 0) {
        writeStoredSelectedCompanyId("");
        return "";
      }

      const hasExistingSelection = safeList.some((company) => company.id === currentSelection);
      if (hasExistingSelection) {
        writeStoredSelectedCompanyId(currentSelection);
        return currentSelection;
      }

      // Keep tenant context stable by selecting the first available tenant when needed.
      const nextSelection = safeList[0].id;
      writeStoredSelectedCompanyId(nextSelection);
      return nextSelection;
    });
  }, []);

  const selectCompany = useCallback((id) => {
    setSelectedCompanyId(id);
    writeStoredSelectedCompanyId(id);
  }, []);

  const addCompany = useCallback((company) => setCompanies((prev) => [...prev, company]), []);
  const updateCompany = useCallback(
    (id, updates) => setCompanies((prev) => prev.map((c) => (c.id === id ? { ...c, ...updates } : c))),
    []
  );

  // Force CompanyInitializer to re-fetch the full list from Firestore.
  const refreshCompanies = useCallback(() => {
    setCompanies([]);
    setRefreshTick((t) => t + 1);
  }, []);

  const selectedCompany = companies.find(c => c.id === selectedCompanyId) || null;

  const value = useMemo(
    () => ({
      companies,
      selectedCompany,
      selectedCompanyId,
      refreshTick,
      selectCompany,
      addCompany,
      updateCompany,
      setCompanyList,
      refreshCompanies,
    }),
    [companies, selectedCompany, selectedCompanyId, refreshTick, selectCompany, addCompany, updateCompany, setCompanyList, refreshCompanies]
  );

  return (
    <CompanyContext.Provider value={value}>
      {children}
    </CompanyContext.Provider>
  );
}

export function useCompany() {
  return useContext(CompanyContext);
}

